import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import { useGetCompaniesList } from "hooks/useInventory"
import {
  getVehicleImporterStaffs,
  createVehicleImporterStaff,
} from "api/vehicleImporterStaffApi"
import { useFormik } from "formik"
import { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import { Button, Col, Form, Label, Row, Spinner } from "reactstrap"

const SENDER_COMPANY = "Aurobil Cars"
const SENDER_COMPANY_PHONE = "01911887731"

const HandoverFieldsModal = ({
  isOpen,
  toggle,
  initialData,
  onConfirm,
  employeesData = [],
}) => {
  const [receiverCompanyId, setReceiverCompanyId] = useState(
    initialData?.receiver?.importer_id ?? null
  )
  const [receiverStaffId, setReceiverStaffId] = useState(
    initialData?.receiver?.staff_id ?? null
  )
  const [companySearch, setCompanySearch] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const prevCompanyRef = useRef(null)

  // Companies with nested staff for receiver selection
  const { data: companiesQuery, isLoading: companiesLoading } =
    useGetCompaniesList({ search: companySearch })
  const companies = useMemo(
    () => companiesQuery?.data?.data ?? companiesQuery?.data ?? [],
    [companiesQuery]
  )

  const companyOptions = useMemo(
    () =>
      companies.map(c => ({
        value: String(c.id),
        label: c.name,
        staff: c.staff ?? [],
        raw: c,
      })),
    [companies]
  )

  const selectedCompany = useMemo(
    () => companies.find(c => String(c.id) === String(receiverCompanyId)) ?? null,
    [companies, receiverCompanyId]
  )

  const staffOptions = useMemo(
    () =>
      (selectedCompany?.staff ?? []).map(s => ({
        value: String(s.id),
        label: s.name,
        raw: s,
      })),
    [selectedCompany]
  )

  const employeeOptions = useMemo(
    () => employeesData.map(e => ({ value: String(e.id), label: e.name })),
    [employeesData]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      sender_employee_id: initialData?.sender?.employee_id
        ? String(initialData.sender.employee_id)
        : "",
      sender_name: initialData?.sender?.name ?? "",
      sender_mobile: initialData?.sender?.mobile ?? "",
      sender_designation: initialData?.sender?.designation ?? "",
      receiver_company_id: initialData?.receiver?.importer_id
        ? String(initialData.receiver.importer_id)
        : "",
      receiver_staff_id: initialData?.receiver?.staff_id
        ? String(initialData.receiver.staff_id)
        : "",
      receiver_name: initialData?.receiver?.name ?? "",
      receiver_mobile: initialData?.receiver?.mobile ?? "",
      receiver_designation: initialData?.receiver?.designation ?? "",
      receiver_company: initialData?.receiver?.company ?? "",
      receiver_company_mobile: initialData?.receiver?.company_mobile ?? "",
      tyre_size: initialData?.tyre_size ?? "",
      issue_1: initialData?.issues?.[0] ?? "",
      issue_2: initialData?.issues?.[1] ?? "",
      issue_3: initialData?.issues?.[2] ?? "",
      issue_4: initialData?.issues?.[3] ?? "",
      issue_5: initialData?.issues?.[4] ?? "",
      issue_6: initialData?.issues?.[5] ?? "",
    },
    onSubmit: async values => {
      setSubmitting(true)
      try {
        let resolvedStaffId = receiverStaffId

        // Check-or-create importer driver when not selected from dropdown
        if (!resolvedStaffId && receiverCompanyId && values.receiver_mobile) {
          const staffListRes = await getVehicleImporterStaffs(receiverCompanyId, {
            all: true,
          })
          const staffList = staffListRes?.data ?? []
          const existing = staffList.find(s =>
            Array.isArray(s.mobile)
              ? s.mobile.includes(values.receiver_mobile)
              : s.mobile === values.receiver_mobile
          )
          if (existing) {
            resolvedStaffId = existing.id
          } else {
            const created = await createVehicleImporterStaff({
              importerId: receiverCompanyId,
              name: values.receiver_name,
              mobile: [values.receiver_mobile],
              designation: values.receiver_designation,
            })
            resolvedStaffId = created?.data?.id ?? null
          }
        }

        const handover = {
          sender: {
            employee_id: values.sender_employee_id
              ? Number(values.sender_employee_id)
              : null,
            name: values.sender_name,
            mobile: values.sender_mobile,
            designation: values.sender_designation,
          },
          receiver: {
            staff_id: resolvedStaffId,
            importer_id: receiverCompanyId ? Number(receiverCompanyId) : null,
            name: values.receiver_name,
            mobile: values.receiver_mobile,
            designation: values.receiver_designation,
            company: values.receiver_company,
            company_mobile: values.receiver_company_mobile,
          },
          tyre_size: values.tyre_size,
          issues: [
            values.issue_1,
            values.issue_2,
            values.issue_3,
            values.issue_4,
            values.issue_5,
            values.issue_6,
          ],
        }

        onConfirm(handover)
        toggle()
      } catch (err) {
        toast.error("Failed to save handover details.")
        console.error(err)
      } finally {
        setSubmitting(false)
      }
    },
  })

  // Auto-fill sender from the selected employee option (no extra fetch)
  useEffect(() => {
    const empId = formik.values.sender_employee_id
    if (!empId) return
    const emp = employeesData.find(e => String(e.id) === String(empId))
    if (!emp) return
    formik.setFieldValue("sender_name", emp.name ?? "")
    formik.setFieldValue(
      "sender_mobile",
      Array.isArray(emp.mobile) ? emp.mobile[0] ?? "" : emp.mobile ?? ""
    )
    formik.setFieldValue("sender_designation", emp.designation?.title ?? "")
  }, [formik.values.sender_employee_id, employeesData])

  // Auto-fill company fields when importer is selected
  useEffect(() => {
    const currentCompanyId = formik.values.receiver_company_id
    if (prevCompanyRef.current === currentCompanyId) return
    prevCompanyRef.current = currentCompanyId

    if (!currentCompanyId) {
      setReceiverCompanyId(null)
      return
    }
    setReceiverCompanyId(Number(currentCompanyId))

    const company = companies.find(c => String(c.id) === currentCompanyId)
    if (!company) return

    formik.setFieldValue("receiver_company", company.name ?? "")
    formik.setFieldValue(
      "receiver_company_mobile",
      Array.isArray(company.mobile)
        ? company.mobile[0] ?? ""
        : company.mobile ?? ""
    )
    // Reset staff when company changes
    formik.setFieldValue("receiver_staff_id", "")
    setReceiverStaffId(null)
  }, [formik.values.receiver_company_id, companies])

  // Auto-fill staff fields when driver is selected
  useEffect(() => {
    const staffId = formik.values.receiver_staff_id
    if (!staffId || !selectedCompany) return

    const staff = selectedCompany.staff?.find(s => String(s.id) === staffId)
    if (!staff) return

    setReceiverStaffId(Number(staffId))
    formik.setFieldValue("receiver_name", staff.name ?? "")
    formik.setFieldValue(
      "receiver_mobile",
      Array.isArray(staff.mobile) ? staff.mobile[0] ?? "" : staff.mobile ?? ""
    )
    formik.setFieldValue("receiver_designation", staff.designation ?? "")
  }, [formik.values.receiver_staff_id, selectedCompany])

  return (
    <ThemeModal
      isOpen={isOpen}
      toggle={toggle}
      title="Hand Over Details"
      size="lg"
    >
      <Form onSubmit={formik.handleSubmit}>
        <Row>
          {/* ── Sender Info ───────────────────────── */}
          <Col sm={12} className="mb-2">
            <h6 className="text-muted fw-semibold border-bottom pb-1">
              Sender Info
            </h6>
          </Col>

          <Col sm={12} className="mb-3">
            <FormikCreatableSelect
              name="sender_employee_id"
              label="Select Employee"
              formik={formik}
              options={employeeOptions}
              placeholder="Search employee..."
            />
          </Col>

          <Col sm={4} className="mb-3">
            <Label>Name</Label>
            <InputField
              name="sender_name"
              value={formik.values.sender_name}
              onChange={formik.handleChange}
              placeholder="Sender name"
            />
          </Col>
          <Col sm={4} className="mb-3">
            <Label>Mobile</Label>
            <InputField
              name="sender_mobile"
              value={formik.values.sender_mobile}
              onChange={formik.handleChange}
              placeholder="Sender mobile"
            />
          </Col>
          <Col sm={4} className="mb-3">
            <Label>Designation</Label>
            <InputField
              name="sender_designation"
              value={formik.values.sender_designation}
              onChange={formik.handleChange}
              placeholder="Sender designation"
            />
          </Col>
          <Col sm={6} className="mb-3">
            <Label>Company</Label>
            <InputField value={SENDER_COMPANY} disabled readOnly />
          </Col>
          <Col sm={6} className="mb-3">
            <Label>Company Mobile</Label>
            <InputField value={SENDER_COMPANY_PHONE} disabled readOnly />
          </Col>

          {/* ── Receiver ──────────── */}
          <Col sm={12} className="mb-2">
            <h6 className="text-muted fw-semibold border-bottom pb-1">
              Receiver
            </h6>
          </Col>

          <Col sm={6} className="mb-3">
            <FormikCreatableSelect
              name="receiver_company_id"
              label="Importer (Company)"
              formik={formik}
              options={companyOptions}
              placeholder="Search importer..."
              isLoading={companiesLoading}
              onInputChange={v => setCompanySearch(v)}
            />
          </Col>
          <Col sm={6} className="mb-3">
            <FormikCreatableSelect
              name="receiver_staff_id"
              label="Importer Driver"
              formik={formik}
              options={staffOptions}
              placeholder={
                selectedCompany ? "Select driver..." : "Select company first"
              }
              isDisabled={!selectedCompany}
            />
          </Col>

          <Col sm={4} className="mb-3">
            <Label>Name</Label>
            <InputField
              name="receiver_name"
              value={formik.values.receiver_name}
              onChange={formik.handleChange}
              placeholder="Receiver name"
            />
          </Col>
          <Col sm={4} className="mb-3">
            <Label>Mobile</Label>
            <InputField
              name="receiver_mobile"
              value={formik.values.receiver_mobile}
              onChange={formik.handleChange}
              placeholder="Receiver mobile"
            />
          </Col>
          <Col sm={4} className="mb-3">
            <Label>Designation</Label>
            <InputField
              name="receiver_designation"
              value={formik.values.receiver_designation}
              onChange={formik.handleChange}
              placeholder="Receiver designation"
            />
          </Col>
          <Col sm={6} className="mb-3">
            <Label>Company</Label>
            <InputField
              name="receiver_company"
              value={formik.values.receiver_company}
              onChange={formik.handleChange}
              placeholder="Company name"
            />
          </Col>
          <Col sm={6} className="mb-3">
            <Label>Company Mobile</Label>
            <InputField
              name="receiver_company_mobile"
              value={formik.values.receiver_company_mobile}
              onChange={formik.handleChange}
              placeholder="Company mobile"
            />
          </Col>

          {/* ── Issues ───────────────────────────── */}
          <Col sm={12} className="mb-2">
            <h6 className="text-muted fw-semibold border-bottom pb-1">
              Issues (উল্লেখিত সমস্যা সমূহ)
            </h6>
          </Col>
          {[1, 2, 3, 4, 5, 6].map(n => (
            <Col sm={6} className="mb-3" key={n}>
              <Label>{n}.</Label>
              <InputField
                name={`issue_${n}`}
                value={formik.values[`issue_${n}`]}
                onChange={formik.handleChange}
                placeholder={`Issue ${n}`}
              />
            </Col>
          ))}
        </Row>

        <div className="text-end">
          <Button
            color="secondary"
            type="button"
            className="me-2"
            onClick={toggle}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button color="success" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-1" /> Saving...
              </>
            ) : (
              "Confirm"
            )}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default HandoverFieldsModal
